/** A generic long-form measure report: one row per subject and metric. */
export interface ComparisonReportOptions {
    subjectField: string
    subjectKeyField?: string
    subjectLabel?: string
    metricField: string
    metricKeyField?: string
    currentField: string
    previousField?: string
    headlineMetrics?: string[]
}
export interface ComparisonValue { current: number; previous: number | null }
export interface ComparisonSubject { key: string; label: string; values: Map<string, ComparisonValue> }
const number = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null

export function comparisonReport(rows: unknown[], options: ComparisonReportOptions) {
    const metrics = new Map<string, string>()
    const subjects = new Map<string, ComparisonSubject>()
    for (const raw of rows) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
        const row = raw as Record<string, unknown>
        const label = row[options.subjectField], metric = row[options.metricField]
        const current = number(row[options.currentField])
        if (typeof label !== 'string' || typeof metric !== 'string' || current === null) continue
        const key = String((options.subjectKeyField && row[options.subjectKeyField]) ?? label)
        const metricKey = String((options.metricKeyField && row[options.metricKeyField]) ?? metric)
        const previous = options.previousField ? number(row[options.previousField]) : null
        metrics.set(metricKey, metric)
        const subject = subjects.get(key) ?? { key, label, values: new Map<string, ComparisonValue>() }
        const existing = subject.values.get(metricKey)
        subject.values.set(metricKey, {
            current: current + (existing?.current ?? 0),
            previous: existing ? existing.previous === null || previous === null ? null : existing.previous + previous : previous,
        })
        subjects.set(key, subject)
    }
    const totals = new Map<string, ComparisonValue>()
    for (const key of metrics.keys()) {
        const cells = [...subjects.values()].map(subject => subject.values.get(key))
        totals.set(key, {
            current: cells.reduce((sum, cell) => sum + (cell?.current ?? 0), 0),
            previous: cells.every(cell => cell && cell.previous !== null)
                ? cells.reduce((sum, cell) => sum + (cell?.previous ?? 0), 0) : null,
        })
    }
    return { metrics, subjects: [...subjects.values()], totals }
}

export function comparisonDelta(value: ComparisonValue): string {
    if (value.previous === null) return ''
    if (value.previous === 0) return value.current === 0 ? 'No change' : `+${value.current.toLocaleString()} from zero`
    const delta = Math.round((value.current - value.previous) / Math.abs(value.previous) * 100)
    return `${delta > 0 ? '+' : ''}${delta}%`
}

export function comparisonCsv(report: ReturnType<typeof comparisonReport>): string {
    const escape = (value: string | number | null) => {
        const text = typeof value === 'string' ? value.replace(/^[\s]*[=+@-]/, "'$&") : value === null ? '' : String(value)
        return `"${text.replaceAll('"', '""')}"`
    }
    const comparisons = report.subjects.some(subject => [...subject.values.values()].some(v => v.previous !== null))
    const columns = [...report.metrics].flatMap(([, label]) => comparisons ? [label, `${label} — previous`] : [label])
    const values = (cells: Map<string, ComparisonValue>) => [...report.metrics.keys()].flatMap(key => {
        const value = cells.get(key)
        return comparisons ? [value?.current ?? null, value?.previous ?? null] : [value?.current ?? null]
    })
    return [['Subject', ...columns], ...report.subjects.map(s => [s.label, ...values(s.values)]), ['Total', ...values(report.totals)]]
        .map(row => row.map(escape).join(',')).join('\r\n')
}
